import React, { useSyncExternalStore } from "react";
import { Clock } from "lucide-react";
import { cn } from "../ui";
import { formatTime, speakTime, urgencyOf } from "./useExamTimer";

/**
 * ExamClock — the countdown, and the only part of the exam screen that
 * re-renders every second (see useExamTimer).
 *
 * Calm until it matters: neutral for most of the paper, amber in the last five
 * minutes, red in the last one. `aria-live="off"` because a screen reader
 * announcing every second makes the paper unreadable; the spoken form is on the
 * label for whoever asks.
 */
const TONE = {
  none: "tw-border-line tw-bg-surface tw-text-content-muted",
  normal: "tw-border-line tw-bg-surface tw-text-content",
  warning: "tw-border-warning/40 tw-bg-warning/10 tw-text-warning-fg",
  critical: "tw-border-danger/40 tw-bg-danger/10 tw-text-danger-fg motion-safe:tw-animate-pulse-soft",
};

export default function ExamClock({ clock, hasLimit, className = "" }) {
  const seconds = useSyncExternalStore(clock.subscribe, clock.get, clock.get);
  const urgency = hasLimit ? urgencyOf(seconds) : "none";

  return (
    <span
      className={cn(
        "tw-inline-flex tw-shrink-0 tw-items-center tw-gap-1.5 tw-rounded-full tw-border tw-border-solid",
        "tw-px-3 tw-py-1 tw-text-sm tw-font-semibold tw-tabular-nums tw-leading-5",
        TONE[urgency],
        className,
      )}
    >
      <Clock size={15} aria-hidden="true" className="tw-shrink-0" />
      <span role="timer" aria-live="off" aria-label={hasLimit ? speakTime(seconds) : "No time limit"}>
        {hasLimit ? formatTime(seconds) : "No limit"}
      </span>
    </span>
  );
}
